import React, { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import { SendAmount } from '@odiupsk/up-api-client'
import { _ } from '../../utils/intl'
import {
  Button,
  ButtonLayout,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Grid,
  GridCol,
  Icon,
  Input,
  ReachDialog,
} from '../../ui-kit'
import { fieldToInputProps, fieldToNumberInputProps } from '../../utils/form/fieldToInputProps'
import { SendCreditForm } from './SendCreditForm'
import { cardsApi, holderAccountsApi } from '../../api'
import { logSuccess } from '../../api/utils'
import { setErrorsFromResult } from '../../utils/extractMessageFromAxiosError'
import { limitTwoFloatingPoints } from '../../utils/forms/transformInput'
import { upAxios } from '../../../config'
import { GetHolderInfoForm } from './GetHolderInfoForm'
import { formatMoney } from '../../utils/format'
import { get } from '../../utils/get'

interface Holder {
  account_number: number
  card_number: number
  first_name: string
  id: string
  last_name: string
  number: string
  balance: number
}

export const SEND_CREDIT_MODAL_ID = 'send-credit-modal'

export const SendCreditModal: React.FC<{
  cardId: string
  holderAccountId: string
  onSuccess: () => void
  close: () => void
}> = observer(({ cardId, holderAccountId, onSuccess, close }) => {
  const [getHolderForm] = useState(() => new GetHolderInfoForm())
  const [sendCreditForm] = useState(() => new SendCreditForm())
  const [step, setStep] = useState(1)
  const [sending, setSending] = useState(false)
  const [holder, setHolder] = useState(undefined as Holder | undefined)

  const sendCredit = async () => {
    const { hasError } = await sendCreditForm.form.validate()
    if (hasError) return console.warn(`form has errors`, sendCreditForm.form.formError)

    const valid = sendCreditForm.serialize()

    setSending(true)
    try {
      await cardsApi.postSendAmount(cardId, {
        amount: Number(valid.amount.replace(',', '.')),
        recipient_holder_id: Number(holder?.account_number),
      })
      logSuccess(_(t`Poslanie kreditu bolo úspešné`))
      onSuccess()
      close()
    } catch (err) {
      setErrorsFromResult<SendAmount>(
        {
          amount: sendCreditForm.fields.amount,
        },
        err
      )
    } finally {
      setSending(false)
    }
  }

  const getHolderInfo = async () => {
    const { hasError } = await getHolderForm.form.validate()

    if (hasError) return console.warn(`form has errors`, getHolderForm.form.formError)

    const valid = getHolderForm.serialize()

    setSending(true)

    try {
      const holderResponse = await upAxios.post(
        `/employees/find?holder_id=${Number(valid.receiverId)}`
      )
      const cardBalanceResponse = await holderAccountsApi.getHolderAccountBalance(holderAccountId)

      setHolder({ ...holderResponse.data, balance: cardBalanceResponse.data })
      setStep(2)
    } catch (err) {
      const errMessages = get(err.response, 'data', 'messages')

      if (errMessages && errMessages.length > 0) {
        getHolderForm.fields.receiverId.setError(errMessages[0].message)
      } else {
        setErrorsFromResult<SendAmount>(
          { recipient_holder_id: getHolderForm.fields.receiverId },
          err
        )
      }
    } finally {
      setSending(false)
    }
  }

  const { amount } = sendCreditForm.fields
  const { receiverId } = getHolderForm.fields

  return (
    <ReachDialog id={SEND_CREDIT_MODAL_ID} onDismiss={close}>
      <>
        <DialogHeader title={_(t`Poslať kredit`)} />

        {step === 1 && (
          <>
            <DialogContent className="mb-large">
              <p className="text-color-grey-600 mb-large">
                <Trans>
                  Zadajte, prosím, číslo držitela stravovacej karty UpDéjeuner. Číslo nájdete na
                  druhej strane karty pod čiarovým kódom.
                </Trans>
              </p>

              <Input
                id="receiver-id"
                label={<Trans>Id príjemcu</Trans>}
                data-testid="recipient"
                {...fieldToInputProps(receiverId)}
              />
              <img
                className="flex align-self-center"
                src="/static/img/card_id.png"
                alt={_(t`card`)}
              />
            </DialogContent>
            <DialogFooter>
              <ButtonLayout direction="horizontal" className="align-items-right">
                <Button onClick={close}>
                  <Trans>Zatvoriť</Trans>
                </Button>
                <Button
                  type="primary"
                  onClick={getHolderInfo}
                  isDisabled={sending}
                  isLoading={sending}
                  data-testid="submit">
                  <Trans>Potvrdiť</Trans>
                </Button>
              </ButtonLayout>
            </DialogFooter>
          </>
        )}

        {step === 2 && (
          <>
            <DialogContent className="mb-large">
              <Grid>
                <GridCol className="h5 mb-small">
                  {holder?.last_name} {holder?.first_name}
                </GridCol>
                <GridCol className=" mb-tiny">
                  <span className="text-color-grey-600">
                    <Trans>Číslo držitela stravovacej karty UpDéjeuner</Trans>:
                  </span>{' '}
                  <strong>{holder?.account_number}</strong>
                </GridCol>

                <GridCol className="mb-large">
                  <span className="text-color-grey-600">
                    <Trans>Váš zostatok</Trans>:
                  </span>{' '}
                  <strong>{holder?.balance && formatMoney(holder?.balance)} €</strong>
                </GridCol>
              </Grid>

              <p className="text-color-grey-600 mb-tiny">
                <Trans>Zadajte prosím sumu.</Trans>
              </p>
              <Input
                id="amount"
                type="number"
                label={<Trans>Suma</Trans>}
                rightAddons={<Icon name="euro" size="medium" />}
                data-testid="amount"
                {...fieldToNumberInputProps(amount, limitTwoFloatingPoints)}
              />
            </DialogContent>
            <DialogFooter>
              <ButtonLayout direction="horizontal" className="align-items-right">
                <Button
                  onClick={() => {
                    setStep(1)
                  }}>
                  <Trans>Späť</Trans>
                </Button>
                <Button
                  type="primary"
                  onClick={sendCredit}
                  isDisabled={sending}
                  isLoading={sending}
                  data-testid="submit">
                  <Trans>Poslať kredit</Trans>
                </Button>
              </ButtonLayout>
            </DialogFooter>
          </>
        )}
      </>
    </ReachDialog>
  )
})

SendCreditModal.displayName = 'SendCreditModal'
